export function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const raw = parts.pop()?.split(";").shift();
    if (!raw) return null;
    // Cookie libraries (e.g. js-cookie) URL-encode reserved characters
    // like `+` and `:` on write. Decode on read so consumers always get
    // the original value (e.g. ISO timestamps with `+00:00`).
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  return null;
}

import {
  IGW_GAMEFRAME_SELECTOR,
  OSW_BUTTON_SELECTOR,
} from "@/constants/host-elements";
import { normalizeLanguage } from "./language";

const USER_ID_ATTR = "data-user-id";
const REGISTRATION_DATE_ATTR = "data-registration-date";
const LANGUAGE_ATTR = "data-language";

// Look up data-* attributes on whichever host element is present:
// OSW exposes a custom button (`#unibo-osw-custom-*`), IGW exposes the
// game container (`#gameframe`). Pages typically use only one — we
// check both so the same helpers work for either variant.
const HOST_ELEMENT_SELECTORS = [OSW_BUTTON_SELECTOR, IGW_GAMEFRAME_SELECTOR];

function getWidgetDataAttribute(attributeName: string): string | null {
  if (typeof document === "undefined") return null;
  for (const selector of HOST_ELEMENT_SELECTORS) {
    const value = document.querySelector(selector)?.getAttribute(attributeName);
    if (value) return value;
  }
  return null;
}

export function getUserIdDataAttribute(): string | null {
  return getWidgetDataAttribute(USER_ID_ATTR);
}

export function getRegistrationDateDataAttribute(): string | null {
  return getWidgetDataAttribute(REGISTRATION_DATE_ATTR);
}

export function getLanguageDataAttribute(): string | null {
  return getWidgetDataAttribute(LANGUAGE_ATTR);
}

export type HostValue =
  | "userId"
  | "registrationDate"
  | "language"
  | "currency"
  | "gameId";

export type HostResolver = (key: HostValue) => string | null;

let hostResolver: HostResolver | null = null;

/**
 * Hand identity/prefs resolution to the embedding surface. When set, it is
 * authoritative — the getters below return its answer instead of their own
 * cookie chain. The overlay installs the legacy `UniboOverlay(settings)`
 * contract here (its cookie names, then the settings) so existing embeds keep
 * working unedited; the widgets install nothing.
 */
export function setHostResolver(resolver: HostResolver | null): void {
  hostResolver = resolver;
}

export function getUserIdCookie(): string | null {
  if (hostResolver) return hostResolver("userId");
  return (
    getCookie("unibo_userId") || getCookie("userId") || getUserIdDataAttribute()
  );
}

/**
 * Language code from cookie, in the canonical wire form (e.g. "en", "de", "pt-br"). Tries common
 * cookie names and falls back to the host element's `data-language` attribute. Language is optional
 * for integrators; if nothing is found we default to `DEFAULT_LANGUAGE` so downstream flows
 * (e.g. the configure message) always have a value to send.
 *
 * Regional variants are preserved rather than truncated to two characters — `pt-BR` and `zh-Hans`
 * are distinct locales the platform serves, and the base language is a *fallback*, not a synonym
 * (see `./language.ts` for the three code forms and #3785 for the bug this caused).
 */
export function getLanguageCookie(): string {
  const raw = hostResolver
    ? hostResolver("language")
    : (getCookie("unibo_language") ??
      getCookie("unibo_lang") ??
      getCookie("language") ??
      getLanguageDataAttribute());

  return normalizeLanguage(raw);
}

export function getCurrencyCookie(): string | null {
  if (hostResolver) return hostResolver("currency");
  return getCookie("unibo_currency") || getCookie("currency");
}

export function getRegistrationDateCookie(): string | null {
  if (hostResolver) return hostResolver("registrationDate");
  return (
    getCookie("unibo_registrationDate") ||
    getCookie("registrationDate") ||
    getRegistrationDateDataAttribute()
  );
}

/** The game the player has open — only the overlay's embed supplies one. */
export function getGameIdCookie(): string | null {
  return hostResolver ? hostResolver("gameId") : null;
}

export function getUserTokenCookie(): string | null {
  return getCookie("unibo_userToken") || getCookie("userToken");
}

export function parseRegistrationDate(
  registrationDate: string | null
): number | null {
  if (!registrationDate) return null;

  try {
    // Check if it's already a number (Unix timestamp in seconds)
    const numValue = Number(registrationDate);
    if (!isNaN(numValue)) {
      // If it's a large number (> year 2000 in seconds), it's already in seconds
      // If it's small (< 10000000000), it might be in milliseconds, convert to seconds
      if (numValue < 10000000000) {
        return Math.floor(numValue / 1000);
      }
      return Math.floor(numValue);
    }

    // Parse ISO format to timestamp in seconds
    const parsedDate = Date.parse(registrationDate);
    if (!isNaN(parsedDate)) {
      return Math.floor(parsedDate / 1000);
    }

    return null;
  } catch (e) {
    console.error("Error parsing registration date", registrationDate, e);
    return null;
  }
}

export function parseRegistrationDateToISO(
  registrationDate: string | null
): string | null {
  if (!registrationDate) return null;

  try {
    const numValue = Number(registrationDate);
    if (!isNaN(numValue) && registrationDate.trim() !== "") {
      // It's a numeric timestamp - determine if it's seconds or milliseconds
      // Year 2000 in seconds = 946684800, in milliseconds = 946684800000
      let timestampMs: number;

      if (numValue < 946684800) {
        // Very small number, likely milliseconds
        timestampMs = numValue;
      } else if (numValue < 946684800000) {
        // Between year 2000 in seconds and year 2000 in milliseconds, assume seconds
        timestampMs = numValue * 1000;
      } else {
        // Very large number, likely milliseconds
        timestampMs = numValue;
      }

      const date = new Date(timestampMs);
      const year = date.getFullYear();

      // If the date is unreasonable, try the other interpretation
      if (year < 2000 || year > 2100) {
        const altTimestampMs =
          numValue < 946684800000 ? numValue * 1000 : numValue / 1000;
        const altDate = new Date(altTimestampMs);
        const altYear = altDate.getFullYear();
        if (altYear >= 2000 && altYear <= 2100) {
          timestampMs = altTimestampMs;
        }
      }

      const finalDate = new Date(timestampMs);

      // Convert to ISO format matching Python's isoformat() output
      // Python produces: "2023-07-30T00:00:00+00:00" (no milliseconds, +00:00 not Z)
      return finalDate
        .toISOString()
        .replace(/\.\d{3}Z$/, "+00:00") // Replace .000Z with +00:00
        .replace(/Z$/, "+00:00"); // Replace trailing Z with +00:00 (if no milliseconds)
    } else {
      // It's already in ISO format string
      // Normalize it to match Python's isoformat() format
      return registrationDate
        .replace(/\.\d{3}Z$/, "+00:00") // Replace .000Z (or .XXXZ) with +00:00
        .replace(/Z$/, "+00:00"); // Replace trailing Z with +00:00 (if no milliseconds)
    }
  } catch (e) {
    console.error(
      "Error parsing registration date to ISO",
      registrationDate,
      e
    );
    return null;
  }
}

export async function generatePlayerHash(
  userId: string | null,
  registrationDate: string | null
): Promise<string | null> {
  if (!userId || !registrationDate) {
    return null;
  }

  const regDateTimestamp = parseRegistrationDate(registrationDate);
  if (!regDateTimestamp) {
    return null;
  }

  // Create hash: sha512(userId.registrationDate)
  const message = `${userId}.${regDateTimestamp}`;
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  const hashBuffer = await crypto.subtle.digest("SHA-512", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  return hashHex;
}
